import React, { useState } from "react";
import type { ResourceWithContext, ResourceType } from "./types";
import StudyResourceService from "../../_services/StudyResourceService";

type ResourceSidePanelProps = {
  isOpen: boolean;
  onClose: () => void;
  resource: ResourceWithContext | null;
  onEdit: () => void;
  isViewMode: boolean;
  resourceTypes: ResourceType[];
};

export default function ResourceSidePanel({
  isOpen,
  onClose,
  resource,
  onEdit,
  isViewMode,
  resourceTypes,
}: ResourceSidePanelProps) {
  const [signingUrl, setSigningUrl] = useState(false);

  if (!resource) return null;

  const isS3Url = (url: string): boolean =>
    url.includes(".s3.") && url.includes("amazonaws.com");

  const handleViewResource = async (e: React.MouseEvent, url: string) => {
    e.preventDefault();

    if (!isS3Url(url)) {
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }

    // Open window synchronously (in user gesture call stack) to avoid popup blocker
    const newWindow = window.open("", "_blank");
    setSigningUrl(true);
    try {
      const signedUrl = await StudyResourceService.getSignedUrl(url);
      if (newWindow) {
        newWindow.location.href = signedUrl;
      }
    } catch (err) {
      console.error("Failed to get signed URL:", err);
      if (newWindow) {
        newWindow.location.href = url;
      }
    } finally {
      setSigningUrl(false);
    }
  };

  const resourceType = resourceTypes.find(
    (rt) => rt.id === resource.studyResourceTypeId
  );
  const subType = resourceType?.subtypes?.find(
    (st) => st.id === resource.studyResourceSubTypeId
  );

  const handleOverlayClick = () => {
    onClose();
  };

  const handlePanelClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <>
      {/* Overlay */}
      <div
        className={`lmb-side-panel-overlay ${isOpen ? "visible" : ""}`}
        onClick={handleOverlayClick}
      />

      {/* Side Panel */}
      <div
        className={`lmb-side-panel ${isOpen ? "open" : ""}`}
        onClick={handlePanelClick}
      >
        {/* Header */}
        <div className="lmb-side-panel-header">
          <div className="lmb-side-panel-title">
            <div className="lmb-side-panel-breadcrumb">
              {resource.chapterName} → {resource.sectionName}
            </div>
            <h3>{resource.name || "Untitled Resource"}</h3>
          </div>
          <button
            className="lmb-side-panel-close"
            onClick={onClose}
            type="button"
            title="Close panel"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="lmb-side-panel-body">
          {/* Resource Type */}
          {resource.resourceType === "test" ? (
            <div className="lmb-side-panel-section">
              <label className="lmb-side-panel-label">Type</label>
              <div className="lmb-side-panel-value">Test</div>
              {resource.testId && (
                <>
                  <label className="lmb-side-panel-label">Test ID</label>
                  <div className="lmb-side-panel-value">{resource.testId}</div>
                </>
              )}
              {resource.testName && (
                <>
                  <label className="lmb-side-panel-label">Test Name</label>
                  <div className="lmb-side-panel-value">{resource.testName}</div>
                </>
              )}
            </div>
          ) : (
            <div className="lmb-side-panel-section">
              <label className="lmb-side-panel-label">Type</label>
              <div className="lmb-side-panel-value">
                {resourceType?.name || `Unknown (ID: ${resource.studyResourceTypeId})`}
              </div>

              {subType && (
                <>
                  <label className="lmb-side-panel-label">Sub-type</label>
                  <div className="lmb-side-panel-value">{subType.name}</div>
                </>
              )}
            </div>
          )}

          {/* URL */}
          {resource.url && (
            <div className="lmb-side-panel-section">
              <label className="lmb-side-panel-label">URL</label>
              <div className="lmb-side-panel-value">
                <a
                  href={resource.url}
                  onClick={(e) => handleViewResource(e, resource.url!)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="lmb-side-panel-link"
                  style={signingUrl ? { pointerEvents: "none", opacity: 0.6 } : undefined}
                >
                  {signingUrl ? "Opening..." : "View Resource"}
                </a>
              </div>
            </div>
          )}

          {/* File URL */}
          {resource.fileUrl && (
            <div className="lmb-side-panel-section">
              <label className="lmb-side-panel-label">File</label>
              <div className="lmb-side-panel-value lmb-side-panel-file">
                {resource.fileUrl}
              </div>
            </div>
          )}

          {/* Content */}
          {resource.content && (
            <div className="lmb-side-panel-section">
              <label className="lmb-side-panel-label">Content</label>
              <div className="lmb-side-panel-content">{resource.content}</div>
            </div>
          )}

          {/* Tags */}
          {resource.tags && resource.tags.length > 0 && (
            <div className="lmb-side-panel-section">
              <label className="lmb-side-panel-label">Tags</label>
              <div className="lmb-side-panel-tags">
                {resource.tags.map((tag) => (
                  <span key={tag.id} className="lmb-resource-tag">
                    {tag.displayLabel || tag.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Resource ID - Debug info */}
          {resource.id && (
            <div className="lmb-side-panel-section lmb-side-panel-debug">
              <label className="lmb-side-panel-label">Resource ID</label>
              <div className="lmb-side-panel-value">
                <a
                  href={`/study-resources?id=${resource.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="lmb-side-panel-id-link"
                  title="Open in Study Resources (new tab)"
                >
                  #{resource.id}
                </a>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {!isViewMode && (
          <div className="lmb-side-panel-footer">
            <button
              className="lmb-btn lmb-btn-primary"
              onClick={onEdit}
              type="button"
            >
              Edit Resource
            </button>
          </div>
        )}
      </div>
    </>
  );
}
